import './Card.scss'
import { useId, type ComponentProps, type MouseEventHandler, type ReactNode } from 'react'
import { MdOutlineArrowForward } from 'react-icons/md'
import { clx } from '../clx'

interface CardBaseProps extends Omit<ComponentProps<'section'>, 'title' | 'onClick'> {
  /** Heading text. Labels the card (`aria-labelledby`) when the root is a `section`. */
  title?: ReactNode
  /** Heading rank for the title. Default 2. */
  headingLevel?: 2 | 3 | 4
  footer?: ReactNode
  /** Body padding. `none` for a table or list that runs edge to edge. Default `md`. */
  padding?: 'none' | 'md'
  /** Root element. A `div` card is not a landmark and is not labelled. Default `section`. */
  as?: 'section' | 'div'
}

export interface StaticCardProps extends CardBaseProps {
  isInteractive?: false
  /** Header slot, end-aligned beside the title (buttons, menus). */
  actions?: ReactNode
  onClick?: ComponentProps<'section'>['onClick']
  href?: never
  icon?: never
}

export interface InteractiveCardProps extends CardBaseProps {
  /**
   * The whole card is one link (`href`) or button (`onClick` only). The
   * focusable element is the title; a pseudo-element stretches its hit area
   * over the card, so the body is clickable but never a second tab stop.
   */
  isInteractive: true
  title: ReactNode
  href?: string
  onClick?: MouseEventHandler<HTMLElement>
  /** Leading media tile (the Settings cards' icon). Decorative. */
  icon?: ReactNode
  actions?: never
}

export type CardProps = StaticCardProps | InteractiveCardProps

export function Card({
  title,
  headingLevel = 2,
  actions,
  footer,
  padding = 'md',
  as = 'section',
  isInteractive = false,
  href,
  onClick,
  icon,
  className,
  children,
  ref,
  ...rest
}: CardProps) {
  const id = useId()
  const titleId = `card-${id}-title`
  const bodyId = `card-${id}-body`
  const Heading = `h${headingLevel}` as const
  const hasTitle = title !== undefined && title !== null && title !== false
  // Typed as section: div and section share the same attributes.
  const Root = as as 'section'

  const target = isInteractive ? (
    href !== undefined ? (
      <a
        className="card__target"
        href={href}
        onClick={onClick}
        {...(children !== undefined && { 'aria-describedby': bodyId })}
      >
        {title}
      </a>
    ) : (
      <button
        type="button"
        className="card__target"
        onClick={onClick}
        {...(children !== undefined && { 'aria-describedby': bodyId })}
      >
        {title}
      </button>
    )
  ) : (
    title
  )

  return (
    <Root
      ref={ref}
      {...rest}
      {...(!isInteractive && onClick && { onClick })}
      {...(hasTitle && as === 'section' && { 'aria-labelledby': titleId })}
      className={clx(
        'card',
        isInteractive && 'card--is-interactive',
        padding === 'none' && 'card--is-flush',
        footer !== undefined && 'card--has-footer',
        className
      )}
    >
      {isInteractive && (
        <div className="card__top">
          {icon !== undefined && (
            <span className="card__icon" aria-hidden="true">
              {icon}
            </span>
          )}
          <span className="card__arrow" aria-hidden="true">
            <MdOutlineArrowForward />
          </span>
        </div>
      )}
      {(hasTitle || actions !== undefined) && (
        <div className="card__header">
          {hasTitle && (
            <Heading id={titleId} className="card__title">
              {target}
            </Heading>
          )}
          {actions !== undefined && <div className="card__actions">{actions}</div>}
        </div>
      )}
      {children !== undefined && (
        <div id={bodyId} className="card__body">
          {children}
        </div>
      )}
      {footer !== undefined && <div className="card__footer">{footer}</div>}
    </Root>
  )
}
